import type { ApiErrorBody, ApiErrorCode, Page } from "@tcr/contracts";
import { API_BASE } from "./endpoints";

/** Client-side codes for failures that never reached (or never came back from) the API. */
export type ClientErrorCode = ApiErrorCode | "NetworkError" | "UnexpectedResponse";

export class ApiError extends Error {
  readonly status: number;
  readonly errorCode: ClientErrorCode;
  readonly requestId: string | null;
  readonly errorData: unknown;

  constructor(status: number, errorCode: ClientErrorCode, message: string, requestId: string | null = null, errorData?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.requestId = requestId;
    this.errorData = errorData;
  }
}

export function isApiError(e: unknown, code?: ClientErrorCode): e is ApiError {
  return e instanceof ApiError && (code === undefined || e.errorCode === code);
}

// ───────────── access token (memory only; refresh token lives in an httpOnly cookie) ─────────────

let accessToken: string | null = null;
let refreshHandler: (() => Promise<string | null>) | null = null;
let refreshInFlight: Promise<string | null> | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}
export function setAccessToken(token: string | null): void {
  accessToken = token;
}
/** Registered by the auth provider: performs POST /auth/refresh and returns the new token. */
export function setRefreshHandler(fn: (() => Promise<string | null>) | null): void {
  refreshHandler = fn;
}

/** Single-flight refresh so N parallel 401s cause one refresh call. */
async function refreshOnce(): Promise<string | null> {
  if (!refreshHandler) return null;
  if (!refreshInFlight) {
    refreshInFlight = refreshHandler().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// ───────────── request ─────────────

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** Multipart upload (body is ignored). */
  form?: FormData;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Return the raw Response (e.g. images/blobs). */
  raw?: boolean;
  /** Skip the automatic refresh-and-retry on 401 (used by auth endpoints themselves). */
  noRetry?: boolean;
  /** Skip the Authorization header. */
  anonymous?: boolean;
}

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = path.startsWith("http") || path.startsWith(API_BASE) ? path : `${API_BASE}${path}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

async function parseError(res: Response): Promise<ApiError> {
  let text = "";
  try {
    text = await res.text();
  } catch {
    text = "";
  }
  return errorFromBody(res.status, res.statusText, text);
}

/** Build an ApiError from a failed response's status and body text (fetch and XHR). */
export function errorFromBody(status: number, statusText: string, text: string): ApiError {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = text ? (JSON.parse(text) as Partial<ApiErrorBody>) : null;
  } catch {
    body = null;
  }
  if (body && typeof body.errorCode === "string") {
    return new ApiError(status, body.errorCode, body.errorMessage ?? statusText, body.requestId ?? null, body.errorData);
  }
  const fallback: ClientErrorCode =
    status === 401
      ? "Unauthenticated"
      : status === 403
        ? "Forbidden"
        : status === 404
          ? "NotFound"
          : status === 409
            ? "Conflict"
            : status === 422
              ? "ValidationFailed"
              : status === 429
                ? "RateLimited"
                : status >= 500
                  ? "InternalError"
                  : "UnexpectedResponse";
  return new ApiError(status, fallback, statusText || `HTTP ${status}`);
}

async function send(path: string, opts: RequestOptions, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: "application/json", ...opts.headers };
  let body: BodyInit | undefined;
  if (opts.form) {
    body = opts.form;
  } else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  if (token && !opts.anonymous) headers.Authorization = `Bearer ${token}`;
  try {
    return await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? (body ? "POST" : "GET"),
      headers,
      body,
      credentials: "include",
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "NetworkError", "Could not reach the platform. Check your network connection.");
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await send(path, opts, accessToken);
  if (res.status === 401 && !opts.noRetry && !opts.anonymous && refreshHandler) {
    const token = await refreshOnce();
    if (token) res = await send(path, opts, token);
  }
  if (!res.ok) throw await parseError(res);
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, "UnexpectedResponse", "The platform returned an unreadable response.");
  }
}

export interface UploadOptions {
  /** Called with (sent bytes, total bytes) while the body uploads. */
  onProgress?: (loaded: number, total: number) => void;
  /** Called once the body is fully sent (the server may still be processing). */
  onUploaded?: () => void;
  signal?: AbortSignal;
}

function xhrUpload(path: string, form: FormData, token: string | null, opts: UploadOptions): Promise<{ status: number; statusText: string; text: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", buildUrl(path));
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/json");
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => opts.onProgress?.(e.loaded, e.lengthComputable ? e.total : 0);
    xhr.upload.onload = () => opts.onUploaded?.();
    xhr.onload = () => resolve({ status: xhr.status, statusText: xhr.statusText, text: xhr.responseText });
    xhr.onerror = () => reject(new ApiError(0, "NetworkError", "Could not reach the platform. Check your network connection."));
    xhr.onabort = () => reject(new DOMException("Upload cancelled", "AbortError"));
    if (opts.signal) {
      if (opts.signal.aborted) return xhr.abort();
      opts.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(form);
  });
}

/**
 * Multipart POST with upload progress (fetch has none). Same auth, single refresh-and-retry
 * on 401 and error parsing as `request`.
 */
export async function uploadForm<T>(path: string, form: FormData, opts: UploadOptions = {}): Promise<T> {
  let res = await xhrUpload(path, form, accessToken, opts);
  if (res.status === 401 && refreshHandler) {
    const token = await refreshOnce();
    if (token) res = await xhrUpload(path, form, token, opts);
  }
  if (res.status < 200 || res.status >= 300) throw errorFromBody(res.status, res.statusText, res.text);
  if (res.status === 204 || !res.text) return undefined as T;
  try {
    return JSON.parse(res.text) as T;
  } catch {
    throw new ApiError(res.status, "UnexpectedResponse", "The platform returned an unreadable response.");
  }
}

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, opts?: RequestOptions) => request<T>(path, { ...opts, method: "GET", query }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: "POST", body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: "PATCH", body }),
  del: <T = void>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: "DELETE" }),
  /** Fetch a binary resource (map image, diagnostic artifact) with auth and return a Blob. */
  blob: async (path: string, opts?: RequestOptions): Promise<Blob> => {
    const res = await request<Response>(path, { ...opts, method: "GET", raw: true });
    return res.blob();
  },
  /** Fetch a binary resource as bytes (e.g. a point-cloud preview). */
  bytes: async (path: string, opts?: RequestOptions): Promise<ArrayBuffer> => {
    const res = await request<Response>(path, { ...opts, method: "GET", raw: true, headers: { Accept: "application/octet-stream", ...opts?.headers } });
    return res.arrayBuffer();
  },
};

/**
 * Normalise list responses. The contract says lists are `Page<T>`, but a few
 * per-robot sub-resources may be returned as plain arrays; accept both so the
 * UI is robust while backend and frontend are integrated.
 */
export function toItems<T>(data: Page<T> | T[] | null | undefined): T[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return Array.isArray(data.items) ? data.items : [];
}

export function toPage<T>(data: Page<T> | T[] | null | undefined): Page<T> {
  if (!data) return { items: [], total: 0, page: 0, size: 0 };
  if (Array.isArray(data)) return { items: data, total: data.length, page: 0, size: data.length };
  return data;
}
